import { Box } from '@rebass/grid';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import { stringifyUrl } from 'query-string';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Booking } from '../../entities/Booking';
import NotFoundError from '../../entities/NotFoundError';
import { usePlannerPageRentalUnitByIdLazyQuery, usePlannerPageRentalUnitLazyQuery } from '../../generated/graphql';
import usePlannerPeriod from '../../hooks/planner/usePlannerPeriod';
import useQueryParams from '../../hooks/useQueryParams';
import { convertToPeriod, getDateStringsFromTrip, withDefaultBookingValues } from '../../utils/trip';
import PlannerView, { Container as PlannerContainer, Header } from '../../components/TripPlanner';
import bookingParamsConfig from '../../constants/bookingParams';
import { useTenantContext } from '../../context/TenantContext';
import baseUrls from '../../constants/baseUrls';
import withAuth from '../../hoc/withAuth';
import useEmbedEvents, { plannerParams } from '../../hooks/useEmbedEvents';
import removeUndefinedEntries from '../../utils/removeUndefinedEntries';
import { decodeQueryParams } from 'use-query-params';
import { GlobalStyle } from '../../components/embed/GlobalStyle';

export const query = gql`
    query PlannerPageRentalUnit($slug: String!) {
        rentalUnit(slug: $slug) {
            id
            brand
            maxOccupancy
            petsAllowed
            firstTripDate
        }
    }

    query PlannerPageRentalUnitById($id: ID!) {
        rentalUnit: rentalUnitById(id: $id) {
            id
            brand
            maxOccupancy
            petsAllowed
            slug
            firstTripDate
        }
    }
`;

const Planner: FC<React.PropsWithChildren<unknown>> = () => {
    const { isReady, locale, query: routerQuery } = useRouter();
    useEmbedEvents();

    const params = routerQuery ? removeUndefinedEntries(decodeQueryParams(plannerParams, routerQuery)) : null;
    const [bookingParams] = useQueryParams(bookingParamsConfig);
    const plannerPeriodProps = usePlannerPeriod(
        convertToPeriod(bookingParams.arrivalDate, bookingParams.departureDate)
    );
    const { brandConfig } = useTenantContext();
    const [booking, setBooking] = useState<Booking>({
        ...bookingParams,
        type: bookingParams.type as Booking['type'], // Ensure type matches the Booking type
        amountAdults: bookingParams.amountAdults ?? 2,
    });

    const [fetchRentalUnit, { called, data, loading }] = usePlannerPageRentalUnitLazyQuery({
        variables: { slug: params?.rentalUnitSlug as string },
    });

    const [fetchUnitById, { data: unitById, called: calledById, loading: loadingById }] =
        usePlannerPageRentalUnitByIdLazyQuery({
            variables: { id: params?.rentalUnitId as string },
        });

    const rentalUnit = data?.rentalUnit ?? unitById?.rentalUnit;
    const unitResolved = !(params?.rentalUnitSlug || params?.rentalUnitId) || !!rentalUnit;

    if (called && !loading && !data?.rentalUnit?.id && params?.rentalUnitSlug) {
        throw new NotFoundError();
    }

    if (calledById && !loadingById && !unitById?.rentalUnit?.brand && params?.rentalUnitId) {
        throw new NotFoundError();
    }

    useEffect(() => {
        if (!called && isReady && params?.rentalUnitSlug) {
            fetchRentalUnit();
        }
        if (!calledById && isReady && params?.rentalUnitId) {
            fetchUnitById();
        }
    }, [called, calledById, fetchRentalUnit, fetchUnitById, isReady, params?.rentalUnitId, params?.rentalUnitSlug]);

    const onSubmit = useCallback(() => {
        const { amountAdults, amountBabies, amountChildren, amountPets, amountYouths, trip } =
            withDefaultBookingValues(booking);

        if (!trip || !rentalUnit?.id || !window.top) {
            return;
        }

        const { arrivalDate, departureDate } = getDateStringsFromTrip(trip);
        // const affiliateHandle = Cookies.get(TRAVELBASE_AFF);
        // const affiliateReference = Cookies.get(TRAVELBASE_AFF_REFERENCE);
        // if (affiliateReference) {
        //     params.affiliateReference = affiliateReference;
        // }
        // if (affiliateHandle) {
        //     params.affiliateHandle = affiliateHandle;
        // }
        window.top.location.href = stringifyUrl({
            url: brandConfig.checkout?.checkoutShouldRedirect
                ? `${baseUrls[rentalUnit?.brand as keyof typeof baseUrls] ?? ''}/${locale}/checkout/redirect`
                : `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/${locale}/checkout/redirect`,
            query: {
                amountAdults: amountAdults.toString(),
                amountBabies: amountBabies.toString(),
                amountChildren: amountChildren.toString(),
                amountPets: amountPets.toString(),
                amountYouth: amountYouths.toString(),
                arrivalDate: arrivalDate.toString(),
                departureDate: departureDate.toString(),
                specialId: trip.specialId ?? undefined,
                rentalUnitSlug: params?.rentalUnitSlug ? params.rentalUnitSlug : unitById?.rentalUnit?.slug ?? '',
                // ...params,
            },
        });
    }, [
        booking,
        brandConfig.checkout?.checkoutShouldRedirect,
        locale,
        params?.rentalUnitSlug,
        rentalUnit?.brand,
        rentalUnit?.id,
        unitById?.rentalUnit?.slug,
    ]);

    const onBookingChange = useCallback((value: Booking) => setBooking(current => ({ ...current, ...value })), []);
    if (!isReady) {
        return null;
    }

    return (
        <>
            <Container>
                <PlannerWrapper>
                    {unitResolved && (
                        <PlannerView
                            {...plannerPeriodProps}
                            booking={booking}
                            setBooking={onBookingChange}
                            slug={params?.rentalUnitSlug ? params.rentalUnitSlug : unitById?.rentalUnit?.slug ?? ''}
                            maxOccupancy={rentalUnit?.maxOccupancy}
                            petsAllowed={rentalUnit?.petsAllowed}
                            initialCenterMonth={
                                rentalUnit?.firstTripDate ? new Date(rentalUnit.firstTripDate) : undefined
                            }
                            hideExplanation
                            iframe
                            onSubmit={onSubmit}
                            hasInlineReservationBar
                        />
                    )}
                </PlannerWrapper>
            </Container>
            <GlobalStyle />
        </>
    );
};

const Container = styled.div`
    position: relative;
    padding: 1px;
`;

const PlannerWrapper = styled(Box)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s - 1}px) {
        height: 100%;
        ${PlannerContainer} {
            margin-top: 0;
            ${Header} {
                padding-top: 0.8rem;
                position: sticky;
                top: 0;
                background: white;
                z-index: 1;
                box-shadow: 0 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.03), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.05);
            }
        }
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        ${PlannerContainer} {
            padding-bottom: calc(var(--booking-bar-height) + 3.2rem);
        }
    }
`;
export default withAuth(Planner, {
    onFail: () => 'ignore',
    withLoadingProp: true,
});
